'use client'

import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import Header from '@/components/Header'
import HeroParallax from '@/components/HeroParallax'
import Footer from '@/components/Footer'
import { getAdventureById } from '@/lib/data'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/free-mode'
import styles from '@/styles/adventure-page.module.css'
import { BreadcrumbSchema, AdventureSchema } from '@/lib/schema'
import { useSwiperCleanup } from '@/hooks/useSwiperCleanup'
import GoogleMapEmbed, { GOOGLE_MAPS_EMBEDS } from '@/components/GoogleMapEmbed'
import BookNowCTA from '@/components/BookNowCTA'


export default function SkiTouringPage() {
  const swiperRef = useSwiperCleanup()
  const thisAdventure = getAdventureById('ski-touring')
  const t = useTranslations('guidedAdventures')
  const tAdv = useTranslations('adventures')
  const tCommon = useTranslations('common')
  const tLodges = useTranslations('lodges')
  const params = useParams()
  const locale = params.locale as string

  if (!thisAdventure) {
    return <div>{t('common.adventureNotFound')}</div>
  }

  return (
    <>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: `/${locale}/` },
        { name: t('index.title'), href: `/${locale}/guided-adventures/` },
        { name: tAdv('ski-touring.title'), href: `/${locale}/guided-adventures/ski-touring/` },
      ]} />
      <AdventureSchema
        name={t('skiTouring.schema.name')}
        description={t('skiTouring.schema.description')}
        image={thisAdventure.heroImage}
        url={`/${locale}/guided-adventures/ski-touring/`}
        activityType={t('skiTouring.schema.activityType')}
        location={t('skiTouring.schema.location')}
        geo={{ latitude: 45.92, longitude: 6.87 }}
      />
      <Header />

      <HeroParallax
        backgroundImage={thisAdventure.heroImage}
        title={tAdv('ski-touring.title')}
        subtitle={tAdv('ski-touring.subtitle')}
        location={tAdv('ski-touring.location')}
      />

      <main className={styles.pageWrapper}>
        <section className={styles.adventureDetails}>
          <div className="container">
            <div className={styles.detailsGrid}>
              <div className={styles.itineraryColumn}>
                <h3>{t('common.itinerary')}</h3>
                <p>{tAdv('ski-touring.intro')}</p>
              </div>

              <div className={styles.mapColumn}>
                <div className={styles.mapWrapper}>
                  <GoogleMapEmbed
                    {...GOOGLE_MAPS_EMBEDS['chamonix']}
                    
                  />
                </div>
              </div>

              <div className={styles.includedColumn}>
                <h3>{t('skiTouring.includedTitle')}</h3>
                <ul>
                  <li>{t('skiTouring.included.item1')}</li>
                  <li>{t('skiTouring.included.item2')}</li>
                  <li>{t('skiTouring.included.item3')}</li>
                  <li>{t('skiTouring.included.item4')}</li>
                  <li>{t('skiTouring.included.item5')}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <BookNowCTA
          variant="inline"
          productId="ski-touring"
          productType="adventure"
          productName={tAdv('ski-touring.title')}
          bookingMode={thisAdventure?.bookingMode || 'direct'}
          bookingUrl="/booking/activities/ski-touring"
        />

        <section className={styles.adventures}>
          <div className="container">
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t('common.personalizedAdventures')}</h2>
              <p className={styles.sectionDescription}>
                {t('common.personalizedDescription')}
              </p>
            </div>

            <div className={styles.adventuresCarousel}>
              <Swiper
                modules={[Navigation]}
                slidesPerView={1}
                spaceBetween={24}
                loop={true}
                grabCursor={true}
                navigation={{
                  nextEl: `.${styles.swiperButtonNext}`,
                  prevEl: `.${styles.swiperButtonPrev}`,
                }}
                breakpoints={{
                  640: { slidesPerView: 2, spaceBetween: 24 },
                  1024: { slidesPerView: 3, spaceBetween: 32 },
                }}
                onSwiper={(swiper) => { swiperRef.current = swiper }}
                className={styles.adventuresSwiper}
              >
                {[
                  { id: 1, src: '/images/adventures/ski-touring/2.webp', alt: t('skiTouring.alts.gallery1') },
                  { id: 2, src: '/images/adventures/ski-touring/3.webp', alt: t('skiTouring.alts.gallery2') },
                  { id: 3, src: '/images/adventures/ski-touring/4.webp', alt: t('skiTouring.alts.gallery3') },
                  { id: 4, src: '/images/adventures/ski-touring/5.webp', alt: t('skiTouring.alts.gallery4') },
                  { id: 5, src: '/images/adventures/ski-touring/6.webp', alt: t('skiTouring.alts.gallery5') },
                ].map((image) => (
                  <SwiperSlide key={`ski-touring-${image.id}`}>
                    <article className={styles.adventureCard}>
                      <div className={styles.adventureImage}>
                        <img src={image.src} alt={image.alt} loading="lazy" />
                      </div>
                    </article>
                  </SwiperSlide>
                ))}
              </Swiper>

              <div className={`${styles.swiperButtonPrev} swiper-button-prev`}></div>
              <div className={`${styles.swiperButtonNext} swiper-button-next`}></div>
            </div>

            <div className={styles.sectionCta}>
              <a href={`/${locale}/guided-adventures/`} className="btn-primary">
                {t('common.exploreAdventures')} &rarr;
              </a>
            </div>
          </div>
        </section>

        <BookNowCTA
          variant="section"
          productId="ski-touring"
          productType="adventure"
          productName={tAdv('ski-touring.title')}
          bookingMode={thisAdventure?.bookingMode || 'direct'}
          bookingUrl="/booking/activities/ski-touring"
        />

        <section className={styles.creamSection}>
          <div className="container">
            <h2>{t('common.whereToStay')}</h2>
            <div className={styles.lodgesGrid}>
              <article className={styles.lodgeCard}>
                <div className={styles.lodgeImage}>
                  <img
                    src="/images/lodges/apex-mountain-penthouse/IMG_2891.webp"
                    alt={`${tLodges('apex-mountain-penthouse.name')} - Ski-in ski-out penthouse in La Parva, Chile`}
                    loading="lazy"
                  />
                </div>
                <div className={styles.lodgeOverlay}>
                  <h3 className={styles.lodgeTitle}>{tLodges('apex-mountain-penthouse.name')}</h3>
                  <div className={styles.lodgeContent}>
                    <p>{tLodges('apex-mountain-penthouse.subtitle')}</p>
                    <a href={`/${locale}/lodges/apex-mountain-penthouse/`} className={styles.lodgeLink}>
                      {t('common.explore')} &rarr;
                    </a>
                  </div>
                </div>
              </article>

              <article className={styles.lodgeCard}>
                <div className={styles.lodgeImage}>
                  <img
                    src="https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=1920&q=80"
                    alt={`${tLodges('apex-parva-cave.name')} - Exclusive ski-in ski-out mountain retreat in La Parva`}
                    loading="lazy"
                  />
                </div>
                <div className={styles.lodgeOverlay}>
                  <h3 className={styles.lodgeTitle}>{tLodges('apex-parva-cave.name')}</h3>
                  <div className={styles.lodgeContent}>
                    <p>{tLodges('apex-parva-cave.subtitle')}</p>
                    <a href={`/${locale}/lodges/apex-parva-cave/`} className={styles.lodgeLink}>
                      {t('common.explore')} &rarr;
                    </a>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <BookNowCTA
          variant="closing"
          productId="ski-touring"
          productType="adventure"
          productName={tAdv('ski-touring.title')}
          bookingMode={thisAdventure?.bookingMode || 'direct'}
          bookingUrl="/booking/activities/ski-touring"
        />

      </main>

      <Footer />
    </>
  )
}
